import { API_URL } from "../config";
import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Loader2, ArrowLeft, KeyRound, Sparkles, CheckCircle2, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const response = await axios.post(`${API_URL}/auth/forgot-password`, { email });
      setMessage(response.data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-4 sm:p-6 lg:p-8 font-sans selection:bg-purple-500/30">
      <div className="max-w-6xl w-full flex flex-col lg:flex-row bg-[#121212] rounded-3xl overflow-hidden border border-white/5 shadow-[0_0_40px_rgba(0,0,0,0.5)]">
        
        {}
        <div className="lg:w-5/12 p-2 hidden md:block">
          <div className="h-full w-full rounded-2xl relative overflow-hidden bg-black border border-white/10 flex flex-col justify-between p-10">
            {}
            <motion.div 
              animate={{ 
                background: [
                  "radial-gradient(circle at 0% 0%, #7e22ce 0%, transparent 50%)",
                  "radial-gradient(circle at 100% 100%, #7e22ce 0%, transparent 50%)",
                  "radial-gradient(circle at 0% 100%, #7e22ce 0%, transparent 50%)",
                  "radial-gradient(circle at 0% 0%, #7e22ce 0%, transparent 50%)"
                ]
              }}
              transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0 opacity-40 mix-blend-screen"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-purple-600/30 via-transparent to-black/80 z-0"></div>
            
            {}
            <div className="relative z-10 flex items-center gap-3">
              <div className="flex items-center gap-2 text-white">
                 <KeyRound className="w-6 h-6 -rotate-45 text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
              </div>
              <span className="text-xl font-bold tracking-tight text-white">Linkey</span>
            </div>

            <div className="relative z-10 my-16">
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-4xl lg:text-5xl font-bold tracking-tight mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-white/70"
              >
                Reset Password
              </motion.h1>
              <p className="text-gray-400 text-sm lg:text-base max-w-sm mb-12">
                Forgotten your password? Enter your email address below and we'll send you a link to get back into your account.
              </p>
            </div>

            <div className="relative z-10 flex items-center gap-2 text-sm text-gray-500 font-medium">
              <Sparkles className="h-4 w-4 text-purple-400" />
              Secure Recovery
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="flex-1 p-8 sm:p-12 lg:p-16 flex flex-col justify-center relative">
          
          <div className="max-w-md w-full mx-auto">
            {/* Mobile Logo */}
            <div className="md:hidden flex items-center gap-3 mb-10">
              <div className="flex items-center gap-2 text-white">
                 <KeyRound className="w-8 h-8 -rotate-45 text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
              </div>
              <span className="text-2xl font-bold tracking-tight text-white">Linkey</span>
            </div>

            <div className="mb-10 text-center md:text-left">
              <h2 className="text-3xl font-bold text-white mb-2">
                Forgot Password
              </h2>
              <p className="text-gray-400 text-sm">
                Enter your email to receive a password reset link.
              </p>
            </div>

            <AnimatePresence>
              {error && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mb-6 text-red-400 text-sm bg-red-500/10 border border-red-500/20 p-4 rounded-xl flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 shrink-0" /> {error}
                </motion.div>
              )}
              {message && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mb-6 text-green-400 text-sm bg-green-500/10 border border-green-500/20 p-4 rounded-xl flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 shrink-0" /> {message}
                </motion.div>
              )}
            </AnimatePresence>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-1.5">Email Address</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-500" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full pl-12 pr-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-white/20 focus:border-white/30 transition-all sm:text-sm"
                    placeholder="e.g. john@example.com"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center gap-2 py-4 px-4 border border-transparent rounded-xl text-sm font-bold text-black bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[#121212] focus:ring-white disabled:opacity-50 transition-all mt-8"
              >
                {loading ? <Loader2 className="animate-spin h-5 w-5" /> : 'Send Reset Link'}
              </button>
            </form>

            <div className="text-center mt-8">
              <Link to="/login" className="inline-flex items-center justify-center text-sm text-gray-400 hover:text-white transition-colors gap-2">
                <ArrowLeft className="w-4 h-4" /> 
                <span className="font-bold">Back to log in</span>
              </Link>
            </div>
            
          </div>
        </div>

      </div>
    </div>
  );
};

export default ForgotPassword;